import classNames from 'classnames'
import { PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { ModalDialog } from 'core-components/library/modalDialog'
import { ReactWidgetContext } from 'core-components/sitebuilder/layout/context/ReactWidgetContext'
import { ICallRequest } from 'dialer/classes/models/ICallRequest'
import { ICallStatus } from 'dialer/classes/models/ICallStatus'
import { MY_CONNECT_TYPE } from 'dialer/classes/models/MyConnectType'
import { dialerService } from 'dialer/classes/services/DialerService'
import {
  getDialerSettings,
  MY_CONNECT_LINE,
  setDialerSettings,
} from 'dialer/components/widgets/public/helpers/DialerSettings'
import { NoConnectLineModalContent } from 'dialer/components/widgets/public/noConnectLine/NoConnectLineModalContent'
import { dialerEventBus, EDialerEventBusEvent } from 'dialer/eventBus/DialerEventBus'
import { fail } from 'dialer/helpers/notification'
import logger from 'src/core/utils/Logger'

import { CallResultModalContent } from './CallResultModalContent'

import styles from './CallablePhoneWrapper.module.scss'

export enum CALL_MODE {
  byIcon = 'byIcon',
  byPhoneClick = 'byPhoneClick',
}

interface IAdditionalAttributes {
  widgetId?: string
  partnerId?: string
  contactProcessedInScout?: boolean
}

interface IProps {
  callMode: CALL_MODE
  phone: string
  type: MY_CONNECT_TYPE
  additionalAttributes?: IAdditionalAttributes
  replacementContent?: React.ReactNode
  className?: string
}

export const getFullPhone = (phoneCode?: string, phoneNumber?: string) =>
  phoneCode && phoneNumber ? `${phoneCode}${phoneNumber}` : ''

export const CallablePhoneWrapper = ({
  children,
  callMode,
  phone,
  type,
  replacementContent,
  additionalAttributes,
  className,
}: PropsWithChildren<IProps>) => {
  const { widgetId } = useContext(ReactWidgetContext)
  const [showCallResultModal, setShowCallResultModal] = useState(false)
  const [showNoConnectLineModal, setShowNoConnectLineModal] = useState(false)
  const [callStatus, setCallStatus] = useState<ICallStatus | undefined>(undefined)
  const [line, setLine] = useState(() => getDialerSettings().line)
  const [isWrapperEnabled, setIsWrapperEnabled] = useState(
    () => getDialerSettings().isCallablePhoneNumbers
  )

  const call = useCallback(
    (type: MY_CONNECT_TYPE) => async (phone: string, connectLine?: MY_CONNECT_LINE) => {
      if (phone) {
        try {
          const payload: ICallRequest = {
            type: type,
            isCallAnyway: true,
            line: connectLine || line,
            call: {
              customerEndpoint: phone,
              inputValue: phone,
              additionalAttributes: additionalAttributes
                ? {
                    ...additionalAttributes,
                    widgetId: additionalAttributes.widgetId || widgetId || undefined,
                  }
                : additionalAttributes,
            },
          }
          setShowCallResultModal(true)
          setCallStatus({ lastCallRequest: payload, initialCustomerEndpoint: phone })
          const res = await dialerService.call(payload)
          setCallStatus({
            success: res.success,
            lastCallRequest: payload,
            errorMessage: res.errorMessage,
            initialCustomerEndpoint: phone,
          })
        } catch (e: any) {
          logger.error(e)
          fail(e.message || 'Failed to call')
        }
      }
    },
    [line, additionalAttributes]
  )

  const renderComponent = useMemo(() => {
    const callIfLineIsSelected = () => {
      if (line) {
        call(type)(phone)
      } else {
        setShowNoConnectLineModal(true)
      }
    }
    if (callMode === CALL_MODE.byIcon) {
      const disabledIcon = !phone
      return (
        <div className={styles.iconModeWrapper}>
          {children}
          <i
            title="call"
            onClick={callIfLineIsSelected}
            className={classNames(
              'hpicon hpicon-telephone2',
              styles.iconModeWrapper__icon,
              disabledIcon ? styles.iconModeWrapper__icon_disabled : ''
            )}
          />
        </div>
      )
    }
    return (
      <div
        onClick={(e) => {
          e.preventDefault()
          callIfLineIsSelected()
        }}
        className={styles.phoneClickModeWrapper}
      >
        {replacementContent || children}
      </div>
    )
  }, [phone, callMode, children, line, type, call, replacementContent])

  useEffect(() => {
    const handlersToUnsubscribe: (() => void)[] = []
    const subscribe = () => {
      handlersToUnsubscribe.push(
        dialerEventBus.subscribe(
          EDialerEventBusEvent.onChangeDialerVisibility,
          (event, isDialerVisible: boolean) => {
            setIsWrapperEnabled(isDialerVisible)
          }
        )
      )
      handlersToUnsubscribe.push(
        dialerEventBus.subscribe(
          EDialerEventBusEvent.onChangeLine,
          (event, line: MY_CONNECT_LINE) => {
            setLine(line)
          }
        )
      )
    }

    subscribe()

    return () => handlersToUnsubscribe.forEach((unsubscribe) => unsubscribe())
  }, [])

  return isWrapperEnabled ? (
    <div className={className || ''}>
      {renderComponent}
      <ModalDialog
        show={showNoConnectLineModal}
        size={'md'}
        content={NoConnectLineModalContent}
        contentProps={{
          onSaveAndCall: async (line: MY_CONNECT_LINE) => {
            Promise.all([
              setDialerSettings({
                ...getDialerSettings(),
                line,
              }).then(() => dialerEventBus.notify(EDialerEventBusEvent.onChangeLine, line)),
              call(type)(phone, line),
            ]).catch(logger.error)
          },
          onClose: () => {
            setShowNoConnectLineModal(false)
          },
        }}
        onClose={() => {
          setShowNoConnectLineModal(false)
        }}
      />
      {callStatus?.lastCallRequest ? (
        <ModalDialog
          show={showCallResultModal}
          onClose={() => {
            setShowCallResultModal(false)
          }}
          size={'md'}
          content={CallResultModalContent}
          contentProps={{
            lastCallRequest: callStatus.lastCallRequest,
            success: callStatus?.success,
            errorMessage: callStatus?.errorMessage,
            call: call(type),
            initialCustomerEndpoint: phone,
          }}
        />
      ) : null}
    </div>
  ) : (
    <div className={className || ''}>{children}</div>
  )
}
